import { Injectable } from '@angular/core';
import { notificationError } from '@components/layout/error/error.utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CampaignService } from '@service/campaign.service';
import { getChatSingleCodesByProductIdMap } from '@store/products/products.selector';
import { State } from '@store/state';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { initCampaignByProductIds, initCampaignByProductIdsFailure, initCampaignByProductIdsSuccess, saveCampaign, saveCampaignFailure, saveCampaignSuccess, scheduleCampaign, scheduleCampaignFailure, scheduleCampaignSuccess, sendCampaign, sendCampaignFailure, sendCampaignSuccess, deleteCampaign, deleteCampaignSuccess, deleteCampaignFailure } from './campaign.action';

@Injectable()
export class CampaignEffect {
  constructor(
    public store: Store<State>,
    public actions$: Actions,
    public campaignService: CampaignService,
  ) { }

  initializeCampaignNameByProductIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initCampaignByProductIds),
      switchMap(({ productIds }) =>
        this.campaignService.getCampaignByProductIds(productIds).pipe(
          map((campaignByProductId) =>
            initCampaignByProductIdsSuccess({ campaignByProductId })
          ),
          catchError((error) =>
            of(initCampaignByProductIdsFailure({ error: notificationError(error) }))
          )
        )
      )
    )
  );

  // We don't want to re-fetch the campaign list after each campaign update, creation or scheduling 
  // because of redirection to the campaign details page after those actions 
  // that already contains the updated campaign data
  sendCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendCampaign),
      switchMap(({ campaign }) =>
        this.campaignService.sendImmediateCampaign(campaign).pipe(
          map(() => sendCampaignSuccess({ campaign })),
          catchError((error) => of(sendCampaignFailure({ error: notificationError(error, true) })))
        )
      )
    )
  );

  saveCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveCampaign),
      switchMap(({ campaign }) =>
        this.campaignService.saveCampaign(campaign).pipe(
          map(() => saveCampaignSuccess({ campaign })),
          catchError((error) => of(saveCampaignFailure({ error: notificationError(error, true) })))
        )
      )
    )
  );

  scheduleCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(scheduleCampaign),
      switchMap(({ campaign }) =>
        this.campaignService.scheduleCampaign(campaign).pipe(
          map(() => scheduleCampaignSuccess({ campaign })),
          catchError((error) => of(scheduleCampaignFailure({ error: notificationError(error, true) })))
        )
      )
    )
  );

  deleteCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCampaign),
      withLatestFrom(this.store.select(getChatSingleCodesByProductIdMap)),
      switchMap(([{ campaignId }, chatCodesByProductId]) =>
        this.campaignService.deleteCampaign(campaignId).pipe(
          switchMap(() => [
            deleteCampaignSuccess({ campaignId }),
            initCampaignByProductIds({ productIds: Object.keys(chatCodesByProductId) }),
          ]),
          catchError((error) => of(deleteCampaignFailure({ error: notificationError(error, true) })))
        )
      )
    )
  );
}
